import { useState } from 'react';

import { __Front as __ } from '@Shared/lib';

import { cn } from '@Blocks/_components/ui/utils';

import type { SearchClass } from '@/_types/Search/Search';
import type { HeaderOptions } from '@/_types/Shared/Header';
import type { MenusType, SiteType } from '@/_types/Shared/SiteDatas';

import { BurgerCloseIcon, BurgerIcon } from '../Icons';
import Logo from './components/Logo';
import NavCard from './components/Nav';
import NavCardMobile from './components/NavMobile';
import Search from './components/Search';

export default function Header({
	site,
	menus,
	context,
}: {
	site: SiteType;
	menus: Partial<MenusType>;
	context: Partial<HeaderOptions>;
}) {
	const [openMenu, setOpenMenu] = useState<boolean>(false);
	const headerVariant = context.headerVariants;

	let classMenu = 'invisible pointer-events-none opacity-0';
	const invisible = 'opacity-0 pointer-events-none';
	const visible = 'opacity-1 pointer-events-auto';

	if (openMenu) {
		classMenu = 'visible pointer-events-auto opacity-1';
	}

	const toogleMenu = () => {
		if (openMenu) {
			setOpenMenu(false);
		} else {
			setOpenMenu(true);
		}
	};

	let topNavBg = 'bg-minos-header-topnav-bg';
	let mainNavBg = 'bg-minos-header-mainnav-bg';

	let topNavInside = '';
	let mainNavInside = '';

	if (headerVariant === 'separation' || headerVariant === 'block') {
		topNavBg = 'bg-minos-global-bg';
		mainNavBg = 'bg-minos-global-bg';
	}

	if (headerVariant === 'block') {
		topNavInside = 'bg-minos-header-topnav-bg rounded-minos-nav-radius mb-2.5 px-6';
		mainNavInside = 'bg-minos-header-mainnav-bg rounded-minos-nav-radius mb-2.5 px-6';
	}

	const logoClass = 'max-h-[60px] max-w-[120px]';

	const searchClass: SearchClass = {
		search: 'minos-header-topnav-search',
		searchBg: 'bg-minos-header-topnav-search-bg',
		searchRadius: 'rounded-minos-header-topnav-search-radius',
		iconColor: 'text-minos-header-topnav-icon-color',
	};

	let separation = false;
	if (headerVariant === 'separation') {
		separation = true;
	}

	let searchMobile = false;
	let values: any[] = [];
	values = values.concat(
		context.mainNav?.elements.left,
		context.mainNav?.elements.center,
		context.mainNav?.elements.right,
		context.topNav?.elements.left,
		context.topNav?.elements.center,
		context.topNav?.elements.right
	);
	values.forEach((item) => {
		if (item.value === 'search') {
			searchMobile = true;
		}
	});

	const emptyTopNav =
		context.topNav?.elements.left.length === 0 &&
		context.topNav?.elements.center.length === 0 &&
		context.topNav?.elements.right.length === 0;

	return (
		<header className='relative z-20 w-full'>
			{/* Mobile only */}
			<div className='block desktop:hidden'>
				<div
					className={cn(
						'minos-grid group relative',
						headerVariant != 'separation' ? 'bg-minos-header-mainnav-bg' : mainNavBg,
						openMenu ? 'fixed top-0 left-0 z-50 w-screen' : ''
					)}
				>
					<div className='col-start-2 col-end-14 flex items-center justify-between gap-8 py-5'>
						<Logo site={site} logo={site.custom_logo} itemClass={logoClass} />
						<button title={__('Menu')} className='group relative' onClick={toogleMenu}>
							<div className={cn('transition-all', openMenu ? invisible : visible)}>
								<BurgerIcon className='inline-block text-minos-header-mainnav-icon-color' />
							</div>
							<div
								className={cn(
									'transition-all absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 ',
									openMenu ? visible : invisible
								)}
							>
								<BurgerCloseIcon className='inline-block text-minos-header-mainnav-icon-color' />
							</div>
						</button>
						{searchMobile && <Search homeUrl={site.url} itemClass={searchClass} />}
					</div>
					<div
						className={cn(
							'transition-all absolute right-0 top-full z-50 w-screen py-6',
							mainNavBg,
							classMenu
						)}
					>
						<div className='minos-grid max-h-[80vh] w-full overflow-y-scroll'>
							<div
								className={cn(
									'col-start-2 col-end-14',
									headerVariant === 'block' ? mainNavInside : mainNavBg
								)}
							>
								{context.mainNav && (
									<NavCardMobile
										ariaAbelledby='main-navigation'
										nav={context.mainNav}
										site={site}
										menus={menus}
										navType={'mainNav'}
										addSeparation={separation}
									/>
								)}
							</div>
							<div
								className={cn(
									headerVariant === 'classic' ? 'col-span-full' : 'col-start-2 col-end-14',
									headerVariant === 'block' ? topNavInside : topNavBg
								)}
							>
								{context.topNav && (
									<NavCardMobile
										ariaAbelledby='second-navigation'
										nav={context.topNav}
										site={site}
										menus={menus}
										navType={'topNav'}
										addSeparation={separation}
									/>
								)}
							</div>
						</div>
					</div>
				</div>
			</div>
			{/* Desktop only */}
			<div className='hidden desktop:block'>
				<div className={cn('minos-grid', topNavBg)}>
					{context.topNav && (
						<NavCard
							nav={context.topNav}
							site={site}
							menus={menus}
							classVariant={topNavInside}
							navType={'topNav'}
							ariaAbelledby='second-navigation'
						/>
					)}
				</div>

				{!emptyTopNav ? (
					<>
						{headerVariant === 'separation' && (
							<div className='minos-grid bg-minos-global-bg'>
								<div className='col-start-2 col-end-14 h-0.5 bg-minos-header-separation-color'></div>
							</div>
						)}
					</>
				) : (
					''
				)}

				<div className={cn('minos-grid', mainNavBg)}>
					{context.mainNav && (
						<NavCard
							nav={context.mainNav}
							site={site}
							menus={menus}
							classVariant={mainNavInside}
							navType={'mainNav'}
							ariaAbelledby='main-navigation'
						/>
					)}
				</div>
			</div>
		</header>
	);
}
